import React, { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { api, authApi, markAuthed } from "@/lib/api";
import { sanitizeSvg as svgSanitize } from "@/lib/sanitize";
import { AuthScreen } from "@/components/AuthScreen";
import { AdminPanel } from "@/components/AdminPanel";
import { LispCode } from "@/components/LispCode";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LotStudio } from "@/components/LotStudio";
import { CogoCalc } from "@/components/CogoCalc";
import { SlopeCalc } from "@/components/SlopeCalc";
import { RoadProfile } from "@/components/RoadProfile";
import { DeliverablesPack } from "@/components/DeliverablesPack";
import { LocalAI } from "@/components/LocalAI";
import { GeneratorGuide } from "@/components/GeneratorGuide";
import { FirstRunTour } from "@/components/FirstRunTour";
import { LispLibrary } from "@/components/LispLibrary";
import { SymbolLibrary } from "@/components/SymbolLibrary";
import { BatchConvert } from "@/components/BatchConvert";
import { PointImport } from "@/components/PointImport";
import { LispDebugger } from "@/components/LispDebugger";
import { CrossPlatform } from "@/components/CrossPlatform";
import { TemplateLayers } from "@/components/TemplateLayers";
import { Profiles } from "@/components/Profiles";
import { Projects } from "@/components/Projects";
import { BaseCommands } from "@/components/BaseCommands";
import { FileManager } from "@/components/FileManager";
import { ClientDashboard } from "@/components/ClientDashboard";
import { LandDev, MP_PROMPT, CONTOURS_PROMPT, GRADING_PROMPT } from "@/components/LandDev";
import { FieldPackage } from "@/components/FieldPackage";
import { DraftOptimizer } from "@/components/DraftOptimizer";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Cube, FilePlus, Trash, Files, Sparkle, Download, Copy,
  Warning, CheckCircle, Quotes, ClockCounterClockwise, CircleNotch, LockSimple,
  LinkSimple, ArrowSquareOut, FilePdf, MapPinLine, Path, DownloadSimple,
  ArrowsClockwise, Wrench, MagnifyingGlass, Ruler, ShieldCheck, Buildings, Plus,
  CaretDown, Question, Compass, UsersThree, Lightbulb, X,
} from "@phosphor-icons/react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const TOOL_ICONS = { MapPinLine, Path, DownloadSimple, ArrowsClockwise };

const sanitizeSvg = (svg) => svgSanitize(svg);

// One-time migration: default new/second targets to Civil 3D 2018 + progeCAD 2024 (dual on).
if (!localStorage.getItem("lf_defaults_v2")) {
  localStorage.setItem("lf_platform", "AutoCAD Civil 3D");
  localStorage.setItem("lf_version", "2018");
  localStorage.setItem("lf_alt_platform", "progeCAD");
  localStorage.setItem("lf_alt_version", "2024");
  localStorage.setItem("lf_dual", "true");
  localStorage.setItem("lf_defaults_v2", "1");
}

const shortPlat = (name) => (name === "AutoCAD Civil 3D" ? "Civil 3D" : name || "");

const UploadBtn = ({ onClick, testid, label }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] text-[var(--primary)] hover:bg-[var(--primary)]/15 transition-colors">
    <FilePlus size={12} weight="bold" /> {label}
  </button>
);

const Panel = ({ title, icon, children, action, testid }) => (  <div className="flex flex-col h-full min-h-0" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 px-3 h-9 border-b border-[var(--border)] bg-[var(--surface-2)] shrink-0">
      <div className="flex items-center gap-2 min-w-0 flex-1 text-[11px] uppercase tracking-[0.15em] text-[var(--text-dim)] font-mono">
        <span className="shrink-0 flex items-center">{icon}</span>
        <span className="truncate" title={typeof title === "string" ? title : undefined}>{title}</span>
      </div>
      <div className="shrink-0 flex items-center">{action}</div>
    </div>
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

const openTool = (id) => setTimeout(() => document.querySelector(`[data-testid="${id}"]`)?.click(), 70);

const NavMenu = ({ label, icon: Icon, testid, items }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="sm" data-testid={testid}
        className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
        <Icon size={14} weight="bold" /> {label} <CaretDown size={11} />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] min-w-[210px]">
      <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-[var(--text-dim)] font-mono">{label}</DropdownMenuLabel>
      <DropdownMenuSeparator className="bg-[var(--border)]" />
      {items.map((it) => (
        <DropdownMenuItem key={it.label} data-testid={it.testid}
          onSelect={() => it.run()}
          className="font-mono text-[13px] cursor-pointer focus:bg-[var(--surface-2)] gap-2">
          {it.icon ? <it.icon size={14} weight="bold" className="text-[var(--primary)] shrink-0" /> : null}
          {it.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);


function Workspace({ user, onLogout }) {
  const [platforms, setPlatforms] = useState([]);
  const [platform, setPlatform] = useState(() => localStorage.getItem("lf_platform") || "AutoCAD Civil 3D");
  const [version, setVersion] = useState(() => localStorage.getItem("lf_version") || "2018");
  const [altPlatform, setAltPlatform] = useState(() => localStorage.getItem("lf_alt_platform") || "progeCAD");
  const [altVersion, setAltVersion] = useState(() => localStorage.getItem("lf_alt_version") || "2024");
  const [triple, setTriple] = useState(() => localStorage.getItem("lf_triple") === "true");
  const [thirdPlatform, setThirdPlatform] = useState(() => localStorage.getItem("lf_third_platform") || "AutoCAD");
  const [thirdVersion, setThirdVersion] = useState(() => localStorage.getItem("lf_third_version") || "2025");
  const [clashStyle, setClashStyle] = useState("suffix");
  const [clashPrefix, setClashPrefix] = useState("");
  const [manuals, setManuals] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [resources, setResources] = useState([]);
  const [tools, setTools] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [deepDig, setDeepDig] = useState(false);
  const [deepCheck, setDeepCheck] = useState(false);
  const [refine, setRefine] = useState(true);
  const [codeView, setCodeView] = useState("repaired");
  const [dual, setDual] = useState(() => localStorage.getItem("lf_dual") !== "false");
  const [scale, setScale] = useState(null);
  const [genStage, setGenStage] = useState("");
  const [genJobId, setGenJobId] = useState(null);
  const [genError, setGenError] = useState("");
  const [keyLow, setKeyLow] = useState(false);
  const [keyLowDismissed, setKeyLowDismissed] = useState(false);

  const refreshKeyStatus = async () => {
    try { const s = await api.aiKeyStatus(); setKeyLow(!!s.low); if (!s.low) setKeyLowDismissed(false); } catch (e) { console.debug("aiKeyStatus check failed (best-effort)", e); }
  };
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(() => localStorage.getItem("lf_client") || "");
  const [newClient, setNewClient] = useState("");
  const [clientDlg, setClientDlg] = useState(false);
  const [clientFilter, setClientFilter] = useState("");
  const [showTour, setShowTour] = useState(() => !localStorage.getItem("lf_tour_done"));
  const cancelRef = useRef(false);
  const contextInput = useRef();
  const templateInput = useRef();

  const refresh = useCallback(async () => {
    const [m, h, c] = await Promise.all([
      api.manuals(clientId || undefined),
      api.history(clientId || undefined),
      api.clients(),
    ]);
    setManuals(m);
    setHistory(h);
    setClients(c);
  }, [clientId]);

  useEffect(() => {
    api.platforms().then(setPlatforms);
    api.resources().then(setResources);
    api.tools().then(setTools);
    api.clients().then((list) => {
      setClients(list);
      if (!localStorage.getItem("lf_client") && list.length) {
        setClientId(list[0].id);
        localStorage.setItem("lf_client", list[0].id);
      }
    });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const switchClient = (id) => {
    setClientId(id);
    localStorage.setItem("lf_client", id);
    setResult(null);
    setPrompt("");
    const c = clients.find((x) => x.id === id);
    setClashStyle("suffix");
    setClashPrefix((c?.name || "").split(/\s+/).map((w) => w[0] || "").join("").slice(0, 4).toUpperCase());
    const d = c?.defaults;
    if (d) {
      if (d.platform) setPlatform(d.platform);
      if (d.version) setVersion(d.version);
      if (d.alt_platform) setAltPlatform(d.alt_platform);
      if (d.alt_version) setAltVersion(d.alt_version);
      if (typeof d.dual === "boolean") setDual(d.dual);
      if (typeof d.triple === "boolean") setTriple(d.triple);
      if (d.third_platform) setThirdPlatform(d.third_platform);
      if (d.third_version) setThirdVersion(d.third_version);
      if (d.scale !== undefined) setScale(d.scale);
    }
  };

  const saveClientDefaults = async () => {
    if (!clientId) return;
    const defaults = {
      platform, version, alt_platform: altPlatform, alt_version: altVersion,
      dual, triple, third_platform: thirdPlatform, third_version: thirdVersion, scale,
    };
    try {
      await api.updateClient(clientId, { defaults });
      await refresh();
      toast.success("Saved as this client's defaults");
    } catch { toast.error("Could not save defaults"); }
  };

  const applyLandDev = (p) => {
    setPlatform("AutoCAD Civil 3D");
    setVersion("2018");
    setDual(true);
    setAltPlatform("progeCAD");
    setAltVersion("2024");
    setPrompt(p);
    if (!clientId) toast.error("Preset loaded — select a client before generating.");
    else toast.success("Land Dev preset loaded — review the prompt and Generate.");
  };

  const applyMpWithBasis = (note) => {
    applyLandDev(`${note}\n\n${MP_PROMPT}`);
  };

  const generateMpWithBasis = (note) => {
    setPlatform("AutoCAD Civil 3D"); setVersion("2018");
    setDual(true); setAltPlatform("progeCAD"); setAltVersion("2024");
    const full = `${note}\n\n${MP_PROMPT}`;
    setPrompt(full);
    handleGenerate({ prompt: full, platform: "AutoCAD Civil 3D", version: "2018" });
  };

  const generateLandDevPreset = (which) => {
    const map = { contours: CONTOURS_PROMPT, grading: GRADING_PROMPT, mp: MP_PROMPT };
    const full = map[which];
    if (!full) return;
    setPlatform("AutoCAD Civil 3D"); setVersion("2018");
    setDual(true); setAltPlatform("progeCAD"); setAltVersion("2024");
    setPrompt(full);
    handleGenerate({ prompt: full, platform: "AutoCAD Civil 3D", version: "2018" });
  };

  const saveClientStandards = async (standards) => {
    if (!clientId) { toast.error("Select a client first."); return; }
    try { await api.updateClient(clientId, { standards }); await refresh(); toast.success("Client standards saved"); }
    catch { toast.error("Could not save standards"); }
  };

  const saveClientPreset = async (preset) => {
    if (!clientId) { toast.error("Select a client first."); return; }
    const id = `custom-${Date.now()}`;
    const next = [...(activeClient?.presets || []), { ...preset, id }];
    try { await api.updateClient(clientId, { presets: next }); await refresh(); toast.success(`Saved "${preset.title}" as a preset`); }
    catch { toast.error("Could not save preset"); }
  };

  const clearWorkspace = () => {
    setResult(null);
    setPrompt("");
    setScale(null);
  };

  const appendPrompt = (text) => {
    if (!text) return;
    setPrompt((prev) => (prev.trim() ? prev.trimEnd() + "\n\n" + text : text));
  };

  const handleCreateClient = async () => {
    const name = newClient.trim();
    if (!name) return;
    try {
      const c = await api.createClient(name);
      setNewClient("");
      setClientDlg(false);
      const list = await api.clients();
      setClients(list);
      switchClient(c.id);
      toast.success(`Client "${c.name}" ready`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create client");
    }
  };

  const handleDeleteClient = async () => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    if (!window.confirm(`Delete client "${c.name}" and all its files & generations? This cannot be undone.`)) return;
    try {
      await api.deleteClient(clientId);
      const list = await api.clients();
      setClients(list);
      switchClient(list[0]?.id || "");
      toast.success(`Deleted "${c.name}"`);
    } catch (err) {
      toast.error("Could not delete client");
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    const t = toast.loading("Syncing online documentation into knowledge base…");
    try {
      const res = await api.syncDocs();
      await refresh();
      toast.success(`Synced ${res.synced} online source${res.synced !== 1 ? "s" : ""} into the knowledge base`, { id: t });
    } catch (err) {
      toast.error("Sync failed", { id: t });
    } finally {
      setSyncing(false);
    }
  };

  const versions = platforms.find((p) => p.name === platform)?.versions || [];
  useEffect(() => {
    if (versions.length && !versions.includes(version)) setVersion(versions[0]);
  }, [platform]); // eslint-disable-line
  useEffect(() => { localStorage.setItem("lf_platform", platform); }, [platform]);
  useEffect(() => { localStorage.setItem("lf_version", version); }, [version]);
  useEffect(() => { localStorage.setItem("lf_alt_platform", altPlatform); }, [altPlatform]);
  useEffect(() => { localStorage.setItem("lf_alt_version", altVersion); }, [altVersion]);
  useEffect(() => { localStorage.setItem("lf_dual", String(dual)); }, [dual]);
  useEffect(() => { localStorage.setItem("lf_triple", String(triple)); }, [triple]);
  useEffect(() => { localStorage.setItem("lf_third_platform", thirdPlatform); }, [thirdPlatform]);
  useEffect(() => { localStorage.setItem("lf_third_version", thirdVersion); }, [thirdVersion]);
  const altVersions = platforms.find((p) => p.name === altPlatform)?.versions || [];
  const thirdVersions = platforms.find((p) => p.name === thirdPlatform)?.versions || [];
  useEffect(() => {
    if (altVersions.length && !altVersions.includes(altVersion)) setAltVersion(altVersions[0]);
  }, [altPlatform]); // eslint-disable-line
  useEffect(() => {
    if (thirdVersions.length && !thirdVersions.includes(thirdVersion)) setThirdVersion(thirdVersions[0]);
  }, [thirdPlatform]); // eslint-disable-line

  const handleUpload = async (e, category) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!clientId) {
      toast.error("Select or create a client first — files are saved under the client.");
      setClientDlg(true);
      e.target.value = "";
      return;
    }
    const label = category === "knowledge" ? "knowledge base" : category === "template" ? "template / layer standard" : "project context";
    const t = toast.loading(`Ingesting ${files.length} file${files.length > 1 ? "s" : ""}…`);
    let ok = 0;
    for (const file of files) {
      try {
        await api.upload(file, category, clientId);
        ok += 1;
      } catch (err) {
        toast.error(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    await refresh();
    if (ok) {
      toast.success(`Added ${ok} file${ok > 1 ? "s" : ""} to ${label}`, { id: t });
      if (category === "template") {
        toast.info("Scanning template layers & styles… they'll auto-attach to your next generation.");
        setTimeout(refresh, 8000);
      }
    } else toast.dismiss(t);
    e.target.value = "";
  };

  const handleDelete = async (id, name) => {
    try {
      await api.deleteManual(id);
      await refresh();
      toast.success(`Removed "${name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleGenerate = async (overrides) => {
    const o = overrides || {};
    const p = o.prompt ?? prompt;
    const plat = o.platform ?? platform;
    const ver = o.version ?? version;
    const sc = o.scale !== undefined ? o.scale : scale;
    if (!p.trim()) { toast.error("Describe what you want the LISP to do."); return; }
    if (!clientId) { toast.error("Select or create a client first."); setClientDlg(true); return; }
    setLoading(true);
    setResult(null);
    setGenError("");
    setGenStage("Starting…");
    cancelRef.current = false;
    try {
      const { job_id } = await api.generate({ prompt: p, platform: plat, version: ver, deep_dig: deepDig, dual, alt_platform: altPlatform, alt_version: altVersion, triple, third_platform: thirdPlatform, third_version: thirdVersion, scale: sc, deep_check: deepCheck, refine, client_id: clientId });
      setGenJobId(job_id);
      const started = Date.now();
      while (Date.now() - started < 1800000) {
        for (let w = 0; w < 2500 && !cancelRef.current; w += 200) { await new Promise((r) => setTimeout(r, 200)); }
        if (cancelRef.current) return;
        const s = await api.generationStatus(job_id);
        if (s?.progress?.stage) setGenStage(s.progress.stage);
        if (s.status === "done") {
          setResult(s.result);
          setCodeView("repaired");
          await refresh();
          toast.success(deepDig ? "AutoLISP generated (deep web dig)" : "AutoLISP generated");
          return;
        }
        if (s.status === "error") { setGenError(s.error || "Generation failed"); toast.error(s.error || "Generation failed"); return; }
        if (s.status === "cancelled") { toast.message("Generation cancelled"); return; }
      }
      toast.message("Still working on a large package in the background — reopen this client's history in a minute to grab it.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Generation failed — please try again.";
      setGenError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setGenJobId(null);
      refreshKeyStatus();
    }
  };

  const cancelGenerate = async () => {
    cancelRef.current = true;
    setLoading(false);
    toast.message("Generation cancelled");
    if (genJobId) { try { await api.cancelJob(genJobId); } catch (err) { console.debug("cancelJob failed (best-effort)", err); } }
  };

  const copyCode = () => {
    navigator.clipboard.writeText((codeView === "raw" && result.code_raw) ? result.code_raw : result.code);
    toast.success("Code copied");
  };

  const openHistory = async (id) => {
    try {
      const g = await api.generation(id);
      setResult(g);
      setCodeView("repaired");
    } catch { toast.error("Could not open this generation."); }
  };

  const handleRenameClash = async () => {
    if (!result?.id) return;
    const t = toast.loading("Renaming clashing commands…");
    try {
      const g = await api.renameClash(result.id, { style: clashStyle, prefix: clashPrefix });
      setResult(g);
      await refresh();
      const pairs = Object.entries(g.renamed || {}).map(([o, n]) => `c:${o}→c:${n}`).join(", ");
      toast.success(pairs ? `Renamed ${pairs}` : "No clashes to rename", { id: t });
    } catch { toast.error("Rename failed", { id: t }); }
  };

  const context = clientId
    ? manuals.filter((m) => (m.category === "context" || m.category === "template") && !m.builtin && m.client_id === clientId)
    : [];
  const activeClient = clients.find((c) => c.id === clientId);
  const clientDefaults = activeClient?.defaults;
  const defaultsDiffer = clientDefaults ? (
    clientDefaults.platform !== platform || clientDefaults.version !== version ||
    clientDefaults.alt_platform !== altPlatform || clientDefaults.alt_version !== altVersion ||
    clientDefaults.dual !== dual || clientDefaults.triple !== triple ||
    clientDefaults.third_platform !== thirdPlatform || clientDefaults.third_version !== thirdVersion ||
    ((clientDefaults.scale ?? null) !== (scale ?? null))
  ) : false;

  useEffect(() => {
    const action = loading ? "Generating…" : result ? `Viewing: ${platform} ${version}` : "Editor";
    document.title = `AutoLISP GEN | ${action}`;
  }, [loading, result, platform, version]);

  const ManualRow = ({ m }) => (
    <div className="group flex items-start justify-between gap-2 px-3 py-2 hover:bg-[var(--surface-2)] border-b border-[var(--border)]/50 transition-colors" data-testid={`manual-${m.id}`}>
      <div className="min-w-0">
        <div className="text-[13px] truncate flex items-center gap-1.5">
          {m.builtin && <LockSimple size={11} className="text-[var(--text-dim)] shrink-0" />}
          {m.name}
        </div>
        <div className="text-[10px] text-[var(--text-dim)] font-mono mt-0.5 flex items-center gap-1.5">
          {m.category === "template" && <span className="text-[var(--primary)]">TEMPLATE ·</span>}
          {m.type.toUpperCase()} · {m.num_chunks} sections
          {m.category === "template" && m.layers?.length > 0 && (
            <span className="text-[var(--success)]" data-testid={`manual-scanned-${m.id}`}>· ✓ {m.layers.length} layers</span>
          )}
        </div>
      </div>
      {!m.builtin && (
        <button onClick={() => handleDelete(m.id, m.name)} data-testid={`delete-manual-${m.id}`}
          className="opacity-0 group-hover:opacity-100 text-[var(--text-dim)] hover:text-[var(--alert)] transition-opacity mt-0.5">
          <Trash size={14} />
        </button>
      )}
    </div>
  );

  const promptDock = (
    <div className="p-3" data-testid="prompt-dock">
      <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="quick-tools">
        <span className="text-[10px] text-[var(--text-dim)] font-mono uppercase tracking-wider flex items-center gap-1"><Wrench size={12} weight="bold" />Tools</span>
        {tools.map((tool) => {
          const Icon = TOOL_ICONS[tool.icon] || Sparkle;
          return (
            <button key={tool.id} onClick={() => appendPrompt(tool.prompt)} data-testid={`tool-${tool.id}`}
              className="flex items-center gap-1.5 h-7 px-2.5 rounded-[3px] border border-[var(--border)] bg-[var(--bg)] text-[11px] font-mono text-[var(--text-dim)] hover:text-[var(--primary)] hover:border-[var(--primary)] transition-colors">
              <Icon size={13} weight="bold" /> {tool.title}
            </button>
          );
        })}
        <span className="text-[10px] text-[var(--text-dim)] font-mono uppercase tracking-wider flex items-center gap-1 ml-1"><Ruler size={12} weight="bold" />Scale</span>
        {[20, 30, 40, 50].map((s) => (
          <button key={s} onClick={() => setScale((v) => v === s ? null : s)} data-testid={`scale-${s}`}
            className={`h-7 px-2.5 rounded-[3px] border text-[11px] font-mono transition-colors ${scale === s ? "border-[var(--primary)] bg-[var(--primary)]/15 text-[var(--primary)]" : "border-[var(--border)] bg-[var(--bg)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}
            title={`Size all text/dimensions/monuments to 1"=${s}' and add a scale up/down command`}>
            1"={s}'
          </button>
        ))}
      </div>
      <Textarea
        value={prompt} onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleGenerate(); }}
        placeholder="e.g. Draw a rectangle from two user-picked points on layer WALLS, then label its area as text at the centroid."
        data-testid="prompt-input"
        className="min-h-[72px] max-h-40 resize-none rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[13px] focus-visible:ring-[var(--primary)] focus-visible:ring-1" />
      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
        <span className="text-[10px] text-[var(--text-dim)] font-mono">Tap Tools / starters to add them to your prompt (they stack — edit freely) · ⌘/Ctrl+Enter</span>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={() => setDual((v) => !v)} data-testid="dual-toggle"
            className={`flex items-center gap-1.5 h-9 px-3 rounded-[3px] border text-[11px] font-mono transition-colors ${dual ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}
            title="Also generate a second .lsp variant for the second CAD platform">
            <Files size={14} weight="bold" /> Dual output{dual ? " · ON" : " · OFF"}
          </button>
          {dual && (
            <>
              <span className="text-[11px] font-mono text-[var(--text-dim)]">2nd →</span>
              <Select value={altPlatform} onValueChange={setAltPlatform}>
                <SelectTrigger className="h-9 w-[140px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" data-testid="alt-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                  {platforms.map((p) => <SelectItem key={p.name} value={p.name} className="font-mono text-[11px]" data-testid={`alt-platform-opt-${p.name}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={altVersion} onValueChange={setAltVersion}>
                <SelectTrigger className="h-9 w-[80px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" data-testid="alt-version-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                  {altVersions.map((v) => <SelectItem key={v} value={v} className="font-mono text-[11px]" data-testid={`alt-version-opt-${v}`}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          <button onClick={() => setTriple((v) => !v)} data-testid="triple-toggle"
            className={`flex items-center gap-1.5 h-9 px-3 rounded-[3px] border text-[11px] font-mono transition-colors ${triple ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}
            title="Also emit a third variant (e.g. plain AutoCAD)">
            +3rd{triple ? " · ON" : ""}
          </button>
          {triple && (
            <>
              <Select value={thirdPlatform} onValueChange={setThirdPlatform}>
                <SelectTrigger className="h-9 w-[140px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" data-testid="third-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                  {platforms.map((p) => <SelectItem key={p.name} value={p.name} className="font-mono text-[11px]" data-testid={`third-platform-opt-${p.name}`}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={thirdVersion} onValueChange={setThirdVersion}>
                <SelectTrigger className="h-9 w-[80px] rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[11px]" data-testid="third-version-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
                  {thirdVersions.map((v) => <SelectItem key={v} value={v} className="font-mono text-[11px]" data-testid={`third-version-opt-${v}`}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          <button onClick={() => setDeepDig((v) => !v)} data-testid="deep-dig-toggle"
            className={`flex items-center gap-1.5 h-9 px-3 rounded-[3px] border text-[11px] font-mono transition-colors ${deepDig ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}
            title="Let the AI search authoritative CAD/LISP sites live while generating">
            <MagnifyingGlass size={14} weight="bold" /> Deep Web Dig{deepDig ? " · ON" : ""}
          </button>
          <button onClick={() => setDeepCheck((v) => !v)} data-testid="deep-check-toggle"
            className={`flex items-center gap-1.5 h-9 px-3 rounded-[3px] border text-[11px] font-mono transition-colors ${deepCheck ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}
            title="Deep load check: flag undefined functions and setq argument mistakes beyond paren balance">
            <ShieldCheck size={14} weight="bold" /> Deep Load Check{deepCheck ? " · ON" : ""}
          </button>
          <button onClick={() => setRefine((v) => !v)} data-testid="refine-toggle"
            className={`flex items-center gap-1.5 h-9 px-3 rounded-[3px] border text-[11px] font-mono transition-colors ${refine ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]"}`}
            title="Multi-pass auto-refine: after generating, the AI lints and self-corrects the routine over 2-3 passes so it compiles on the first try">
            <Sparkle size={14} weight="bold" /> Auto-Refine{refine ? " · ON" : " · OFF"}
          </button>
          <Button onClick={() => handleGenerate()} disabled={loading} data-testid="generate-lisp-btn"
            className="h-9 rounded-[3px] gap-2 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white font-medium px-5">
            {loading ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
            Generate LISP
          </Button>
          {loading && (
            <button onClick={cancelGenerate} data-testid="cancel-generate-btn"
              className="h-9 px-3 rounded-[3px] border border-[var(--alert)]/60 text-[var(--alert)] text-[11px] font-mono hover:bg-[var(--alert)]/10 transition-colors">
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );


  return (
    <div className="h-screen overflow-hidden flex flex-col bg-[var(--bg)] text-[var(--text)]">
      <Toaster theme="dark" position="top-right" closeButton duration={20000} toastOptions={{ className: "cursor-pointer", style: { cursor: "pointer" } }} />
      <FirstRunTour open={showTour} onClose={() => { localStorage.setItem("lf_tour_done", "1"); setShowTour(false); }} />

      {/* Top bar */}
      <header className="flex items-center gap-x-3 gap-y-1.5 flex-wrap min-h-12 py-1.5 px-4 border-b border-[var(--border)] bg-[var(--surface)] shrink-0 z-20">
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="w-6 h-6 flex items-center justify-center bg-[var(--primary)] rounded-[3px]">
            <Cube size={16} weight="fill" className="text-white" />
          </div>
          <span className="font-mono font-bold tracking-tight text-[15px]">LispForge</span>
          <span className="text-[10px] text-[var(--text-dim)] font-mono uppercase tracking-widest hidden sm:inline">AI CAD Automation</span>
        </div>
        <div className="flex items-center gap-2 ml-auto shrink-0 order-last sm:order-none" data-testid="user-bar">
          {user?.role === "admin" && <AdminPanel />}
          <button onClick={() => setShowTour(true)} data-testid="help-tour-btn" title="Take the guided tour"
            className="h-8 w-8 flex items-center justify-center rounded-[3px] text-[var(--text-dim)] hover:text-[var(--primary)] hover:bg-[var(--surface-2)] transition-colors">
            <Question size={16} weight="bold" />
          </button>
          <span className="text-[11px] font-mono text-[var(--text-dim)] hidden md:inline max-w-[160px] truncate" title={user?.email} data-testid="user-email">{user?.email}</span>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn"
            className="h-8 rounded-[3px] text-xs font-mono text-[var(--text-dim)] hover:text-[var(--alert)]">Sign out</Button>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 pl-2 sm:border-l border-[var(--border)]" data-testid="client-bar">
          <Buildings size={15} weight="bold" className="text-[var(--primary)] shrink-0" />
          <Select value={clientId} onValueChange={switchClient}>
            <SelectTrigger className="h-8 w-[150px] xl:w-[180px] rounded-[3px] bg-[var(--surface-2)] border-[var(--border)] font-mono text-xs" data-testid="client-select">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
              <div className="p-1.5 sticky top-0 bg-[var(--surface-2)] z-10 border-b border-[var(--border)]">
                <input value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()} placeholder="Search clients…" data-testid="client-search"
                  className="w-full h-7 px-2 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
              </div>
              {clients.length === 0 && <div className="px-2 py-1.5 text-[11px] text-[var(--text-dim)] font-mono">No clients yet</div>}
              {clients.filter((c) => c.name.toLowerCase().includes(clientFilter.toLowerCase())).map((c) => (
                <SelectItem key={c.id} value={c.id} className="font-mono text-xs" data-testid={`client-opt-${c.id}`}>
                  {c.name} · {c.file_count}f/{c.gen_count}g
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Dialog open={clientDlg} onOpenChange={setClientDlg}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="new-client-btn"
                className="h-8 rounded-[3px] gap-1 text-xs font-mono text-[var(--primary)] hover:bg-[var(--primary)]/10 px-2">
                <Plus size={13} weight="bold" /> New
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-sm rounded-[4px]" data-testid="new-client-dialog">
              <DialogHeader>
                <DialogTitle className="font-mono text-base flex items-center gap-2"><Buildings size={18} weight="bold" className="text-[var(--primary)]" />New Client</DialogTitle>
                <DialogDescription className="text-[var(--text-dim)] text-xs">Files and generations are saved under this client's name and reused to keep future routines consistent with their existing commands.</DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2">
                <input autoFocus value={newClient} onChange={(e) => setNewClient(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreateClient(); }}
                  placeholder="e.g. Acme Surveying" data-testid="new-client-input"
                  className="flex-1 h-9 px-3 rounded-[3px] bg-[var(--bg)] border border-[var(--border)] font-mono text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
                <Button onClick={handleCreateClient} data-testid="create-client-btn"
                  className="h-9 rounded-[3px] bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
          {clientId && (
            <button onClick={handleDeleteClient} data-testid="delete-client-btn" title="Delete this client and all its data"
              className="h-8 px-1.5 rounded-[3px] text-[var(--text-dim)] hover:text-[var(--alert)] transition-colors">
              <Trash size={14} />
            </button>
          )}
          {clientId && (
            <div className="relative flex items-center">
              {defaultsDiffer && <span data-testid="defaults-diff-badge" title="Current targets/scale differ from this client's saved defaults" className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-[var(--surface)] z-10 pointer-events-none" />}
              <ClientDashboard clientId={clientId} clientName={clients.find((c) => c.id === clientId)?.name} onSaveDefaults={saveClientDefaults} standards={activeClient?.standards} onSaveStandards={saveClientStandards} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap ml-auto justify-end" data-testid="nav-tool-menus">
          <NavMenu label="Generate" icon={Sparkle} testid="nav-generate" items={[
            { label: "Land Dev presets", testid: "nav-landdev", icon: Compass, run: () => openTool("landdev-btn") },
            { label: "Field Analysis", testid: "nav-field", icon: MapPinLine, run: () => openTool("field-package-btn") },
            { label: "Optimize (ideas)", testid: "nav-optimize", icon: Lightbulb, run: () => openTool("draft-optimizer-btn") },
            { label: "Cross-Platform convert", testid: "nav-cross", icon: ArrowsClockwise, run: () => openTool("cross-platform-btn") },
            { label: "Batch Convert base", testid: "nav-batch", icon: Files, run: () => openTool("batch-convert-btn") },
          ]} />
          <NavMenu label="Survey Tools" icon={MapPinLine} testid="nav-survey" items={[
            { label: "Lot Studio", testid: "nav-lot", icon: Path, run: () => openTool("lot-studio-btn") },
            { label: "COGO Calculator", testid: "nav-cogo", icon: Wrench, run: () => openTool("cogo-calc-btn") },
            { label: "Slope / Grade", testid: "nav-slope", icon: Wrench, run: () => openTool("slope-calc-btn") },
            { label: "Road Profile", testid: "nav-profile", icon: Wrench, run: () => openTool("road-profile-btn") },
            { label: "Deliverables Pack", testid: "nav-deliverables", icon: Wrench, run: () => openTool("deliverables-btn") },
            { label: "Point Import", testid: "nav-points", icon: MapPinLine, run: () => openTool("point-import-btn") },
            { label: "Template Layers", testid: "nav-template", icon: Files, run: () => openTool("template-layers-btn") },
            { label: "Base Commands", testid: "nav-basecmds", icon: Wrench, run: () => openTool("base-commands-btn") },
            { label: "Symbol Library", testid: "nav-symbols", icon: MapPinLine, run: () => openTool("symbol-library-btn") },
            { label: "LISP Debugger", testid: "nav-debugger", icon: ShieldCheck, run: () => openTool("lisp-debugger-btn") },
          ]} />
          <NavMenu label="Client" icon={UsersThree} testid="nav-client" items={[
            { label: "Projects", testid: "nav-projects", icon: Files, run: () => openTool("projects-btn") },
            { label: "Profiles", testid: "nav-profiles", icon: Buildings, run: () => openTool("profiles-btn") },
          ]} />
          <NavMenu label="Help" icon={Question} testid="nav-help" items={[
            { label: "Resources & sync", testid: "nav-resources", icon: LinkSimple, run: () => openTool("resources-btn") },
            { label: "Local AI (Ollama)", testid: "nav-localai", icon: Sparkle, run: () => openTool("local-ai-btn") },
            ...(user?.role === "admin" ? [{ label: "LISP Library (admin)", testid: "nav-library", icon: Files, run: () => openTool("library-lisp-btn") }] : []),
            { label: "Take the tour", testid: "nav-tour", icon: Lightbulb, run: () => setShowTour(true) },
          ]} />
          <div className="hidden" aria-hidden="true" data-testid="nav-tool-mounts">
          {user?.role === "admin" && <LispLibrary />}
          <LocalAI clientId={clientId} clientName={activeClient?.name} onSaved={refresh} />
          <Projects currentResult={result} onLoad={(p) => { setResult(p.result); if (p.prompt) setPrompt(p.prompt); }} />
          <LandDev onApply={applyLandDev} clientId={clientId}
            clientPresets={activeClient?.presets || []}
            clients={clients}
            clientStandards={activeClient?.standards}
            onSavePresets={async (presets) => {
              if (!clientId) return;
              try { await api.updateClient(clientId, { presets }); await refresh(); toast.success("Preset saved to client"); }
              catch { toast.error("Could not save preset"); }
            }}
            onCopyPresetsTo={async (targetId) => {
              const target = clients.find((c) => c.id === targetId);
              if (!target) return;
              const src = activeClient?.presets || [];
              const existingTitles = new Set((target.presets || []).map((p) => p.title));
              const added = src.filter((p) => !existingTitles.has(p.title))
                .map((p, i) => ({ ...p, id: `custom-${Date.now()}-${i}` }));
              const merged = [...(target.presets || []), ...added];
              try {
                await api.updateClient(targetId, { presets: merged });
                await refresh();
                toast.success(`Copied ${added.length} preset${added.length === 1 ? "" : "s"} to "${target.name}"`);
              } catch { toast.error("Could not copy presets"); }
            }}
            onAttachFinished={() => { if (!clientId) { toast.error("Select a client first."); return; } templateInput.current.click(); }} />
          <FieldPackage clientId={clientId} onUseBasis={applyMpWithBasis} onGenerateBasis={generateMpWithBasis} onGeneratePreset={generateLandDevPreset} />
          <DraftOptimizer clientId={clientId} onSavePreset={saveClientPreset} />
          <LotStudio onUsePrompt={setPrompt} clientName={activeClient?.name} clientStandards={activeClient?.standards} />
          <CogoCalc onUsePrompt={setPrompt} clientStandards={activeClient?.standards} />
          <SlopeCalc />
          <RoadProfile onUsePrompt={setPrompt} clientName={activeClient?.name} clientStandards={activeClient?.standards} />
          <DeliverablesPack clientName={activeClient?.name} clientStandards={activeClient?.standards} />
          <PointImport onUsePrompt={setPrompt} />
          <TemplateLayers onUsePrompt={setPrompt} />
          <Profiles currentScale={scale} onApply={({ scale: s, directive }) => { setScale(s); if (directive) setPrompt((p) => directive + (p ? "\n\n" + p : "")); }} />
          <BaseCommands onInsert={(c) => setPrompt((p) => (p ? p + " " : "") + `c:${c}`)} />
          <SymbolLibrary />
          <BatchConvert />
          <LispDebugger platform={platform} version={version} />
          <CrossPlatform seedCode={result?.code || ""} />
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="resources-btn"
                className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
                <LinkSimple size={14} weight="bold" /> Resources
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-lg rounded-[4px]" data-testid="resources-dialog">
              <DialogHeader>
                <DialogTitle className="font-mono text-base flex items-center gap-2"><LinkSimple size={18} weight="bold" className="text-[var(--primary)]" />Reference Libraries & Providers</DialogTitle>
                <DialogDescription className="text-[var(--text-dim)] text-xs">Curated CAD/AutoLISP references you can open, or sync into the knowledge base.</DialogDescription>
              </DialogHeader>
              <div className="flex items-center justify-between gap-2 p-2.5 border border-[var(--border)] rounded-[3px] bg-[var(--surface-2)]">
                <div className="text-[11px] text-[var(--text-dim)] leading-snug">Pull the linked docs (Lee Mac, AfraLISP, CADTutor, Civil 3D…) into the knowledge base so generation keeps improving.</div>
                <Button size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-docs-btn"
                  className="h-8 rounded-[3px] gap-1.5 text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white shrink-0">
                  {syncing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
                  Sync Online Docs
                </Button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto -mr-2 pr-2">
                {resources.map((cat) => (
                  <div key={cat.category} className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--primary)] font-mono mb-2">{cat.category}</div>
                    {cat.links.map((l) => (
                      <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`resource-link-${l.title}`}
                        className="group flex items-start gap-2 px-3 py-2 border border-[var(--border)] rounded-[3px] mb-1.5 hover:border-[var(--primary)] hover:bg-[var(--surface-2)] transition-colors">
                        <ArrowSquareOut size={14} className="text-[var(--text-dim)] group-hover:text-[var(--primary)] mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium truncate group-hover:text-[var(--primary)]">{l.title}</div>
                          <div className="text-[11px] text-[var(--text-dim)] leading-snug">{l.note}</div>
                        </div>
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-[var(--text-dim)] font-mono uppercase tracking-wider hidden md:inline">Target</span>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="h-8 w-[150px] xl:w-[190px] rounded-[3px] bg-[var(--surface-2)] border-[var(--border)] font-mono text-xs" data-testid="platform-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
              {platforms.map((p) => (
                <SelectItem key={p.name} value={p.name} className="font-mono text-xs" data-testid={`platform-opt-${p.name}`}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={version} onValueChange={setVersion}>
            <SelectTrigger className="h-8 w-[90px] rounded-[3px] bg-[var(--surface-2)] border-[var(--border)] font-mono text-xs" data-testid="version-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--surface-2)] border-[var(--border)] text-[var(--text)]">
              {versions.map((v) => (
                <SelectItem key={v} value={v} className="font-mono text-xs" data-testid={`version-opt-${v}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          </div>
        </div>
      </header>

      {/* Body grid */}
      <div className="flex-1 grid grid-cols-12 min-h-0">
        {/* Left: project files (LLM knowledge base is intentionally hidden — it powers the AI only) */}
        <aside className="col-span-3 lg:col-span-2 min-w-0 border-r border-[var(--border)] flex flex-col min-h-0 bg-[var(--surface)]">
          <input type="file" multiple ref={contextInput} className="hidden" onChange={(e) => handleUpload(e, "context")}
            data-testid="context-file-input" />
          <input type="file" multiple ref={templateInput} className="hidden" onChange={(e) => handleUpload(e, "template")}
            data-testid="template-file-input" />

          <div className="flex-1 min-h-0 flex flex-col">
            <Panel title={clientId ? `Files · ${clients.find((c) => c.id === clientId)?.name || "Client"}` : "Project Files"} testid="context-panel"
              icon={<Files size={13} weight="bold" />}
              action={
                <div className="flex items-center gap-1.5">
                  {clientId && <FileManager clientId={clientId} clients={clients} onChanged={refresh} />}
                  <UploadBtn onClick={() => contextInput.current.click()} testid="add-context-btn" label="Add" />
                </div>
              }>
              <ScrollArea className="h-full scroll-block">
                {context.length === 0 && (
                  <div className="p-3 text-[11px] text-[var(--text-dim)] leading-relaxed">
                    {clientId
                      ? "Add this client's project files — existing LISP, DWG/DXF, CSV point files, a legal description, or a finished-CAD template to match layers. They're saved under the client and reused on every generation."
                      : "Select or create a client in the top bar first. Files are saved under the client's name in the background."}
                  </div>
                )}
                {context.map((m) => <ManualRow key={m.id} m={m} />)}
                <button onClick={() => contextInput.current.click()} data-testid="add-context-row"
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[12px] text-[var(--text-dim)] hover:text-[var(--primary)] hover:bg-[var(--surface-2)] transition-colors border-b border-dashed border-[var(--border)]/60">
                  <FilePlus size={14} /> Add LISP / DWG / DXF / CSV / any files
                </button>
                <button onClick={() => templateInput.current.click()} data-testid="add-template-row"
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-[12px] text-[var(--text-dim)] hover:text-[var(--primary)] hover:bg-[var(--surface-2)] transition-colors border-b border-dashed border-[var(--border)]/60">
                  <Files size={14} /> Add template / finished CAD (match layers)
                </button>
              </ScrollArea>
            </Panel>
          </div>
        </aside>

        {/* Center: editor + prompt */}
        <main className="col-span-9 lg:col-span-7 min-w-0 flex flex-col min-h-0 border-r border-[var(--border)]">
          <div className="flex-1 min-h-0 bg-[var(--surface)] relative">
            {keyLow && !keyLowDismissed && (
              <div className="m-3 rounded-[4px] border border-[var(--warn)]/50 bg-[var(--warn)]/10 p-3 flex items-start gap-2.5" data-testid="key-low-banner">
                <Warning size={18} weight="fill" className="text-[var(--warn)] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[13px] text-[var(--text)] leading-relaxed">AI key balance is low or empty — a recent generation was blocked by the budget limit.</div>
                  <div className="font-mono text-[11px] text-[var(--text-dim)] mt-1">Top up in <b>Profile → Manage plan → Universal Key → Add Balance</b> (or enable auto top-up). This clears automatically after your next successful generation.</div>
                </div>
                <button onClick={() => setKeyLowDismissed(true)} data-testid="key-low-dismiss" className="text-[var(--text-dim)] hover:text-[var(--text)] shrink-0"><X size={15} /></button>
              </div>
            )}
            {!loading && !result && (
              <>
              {genError && (
                <div className="mb-4 rounded-[4px] border border-[var(--alert)]/40 bg-[var(--alert)]/10 p-3 flex items-start gap-2.5" data-testid="gen-error-banner">
                  <Warning size={18} weight="fill" className="text-[var(--alert)] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[13px] text-[var(--text)] leading-relaxed">{genError}</div>
                    <div className="font-mono text-[11px] text-[var(--text-dim)] mt-1">Your prompt is saved below — fix the issue and hit Generate again.</div>
                    <button onClick={() => handleGenerate()} data-testid="gen-error-retry"
                      className="mt-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[3px] bg-[var(--alert)] text-white font-mono text-[11px] hover:opacity-90 transition-opacity">
                      <ArrowsClockwise size={13} weight="bold" /> Try again
                    </button>
                  </div>
                  <button onClick={() => setGenError("")} data-testid="gen-error-dismiss" className="text-[var(--text-dim)] hover:text-[var(--text)] shrink-0"><X size={15} /></button>
                </div>
              )}
              <GeneratorGuide
                clients={clients}
                clientId={clientId}
                onSelectClient={switchClient}
                onCreateClient={async (name) => {
                  try {
                    const c = await api.createClient(name);
                    const list = await api.clients();
                    setClients(list);
                    switchClient(c.id);
                    toast.success(`Client "${c.name}" ready`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not create client");
                  }
                }}
                fileCount={context.length}
                files={context}
                onAddInput={() => { if (!clientId) { toast.error("Select or create a client first."); return; } contextInput.current.click(); }}
                onAddOutput={() => { if (!clientId) { toast.error("Select or create a client first."); return; } templateInput.current.click(); }}
                onDeleteFile={handleDelete}
                tools={tools}
                onTool={(p) => appendPrompt(p)}
                platform={platform}
                version={version}
                targetLabel={`${shortPlat(platform)} ${version}`}
                onStarter={(text) => {
                  if (text) appendPrompt(text);
                  setTimeout(() => {
                    const el = document.querySelector('[data-testid="prompt-input"]');
                    if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
                  }, 60);
                }}
              >
                {promptDock}
              </GeneratorGuide>
              </>
            )}
            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid="loading-state">
                <CircleNotch size={32} className="text-[var(--primary)] animate-spin" />
                <div className="font-mono text-sm text-[var(--text-dim)] mt-3" data-testid="gen-stage">{genStage || "Retrieving documentation & generating…"}</div>
                {dual && <div className="font-mono text-[11px] text-[var(--text-dim)]/70 mt-1">Dual output runs two passes — this can take a couple of minutes.</div>}
              </div>
            )}
            {result && (
              <Tabs defaultValue="code" className="h-full flex flex-col fade-in">
                <div className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--surface-2)] pr-2 shrink-0">
                  <TabsList className="bg-transparent h-9 p-0 gap-0 rounded-none">
                    {[
                      ["code", "Code"], ["diagram", "Diagram"], ["usage", "How to Use"],
                      ["explanation", "Explanation"], ["warnings", "Warnings"],
                      ...(result.code_alt ? [["alt", `${shortPlat(result.alt_platform || "progeCAD")} ${result.alt_version || result.version}`]] : []),
                      ...(result.code_third ? [["third", `${shortPlat(result.third_platform || "AutoCAD")} ${result.third_version || ""}`]] : []),
                    ].map(([t, label]) => (
                      <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
                        className="rounded-none h-9 px-3.5 font-mono text-xs data-[state=active]:bg-[var(--surface)] data-[state=active]:border-b-2 data-[state=active]:border-[var(--primary)] data-[state=active]:text-[var(--text)] text-[var(--text-dim)]">
                        {label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="flex items-center gap-1.5">
                    {result.code_raw && (
                      <div className="flex items-center rounded-[3px] border border-[var(--border)] overflow-hidden" data-testid="code-view-toggle" title="View the auto-repaired code or the model's raw output">
                        <button onClick={() => setCodeView("repaired")} data-testid="code-view-repaired"
                          className={`h-7 px-2 text-[10px] font-mono ${codeView === "repaired" ? "bg-[var(--primary)]/15 text-[var(--primary)]" : "text-[var(--text-dim)]"}`}>Repaired</button>
                        <button onClick={() => setCodeView("raw")} data-testid="code-view-raw"
                          className={`h-7 px-2 text-[10px] font-mono ${codeView === "raw" ? "bg-[var(--primary)]/15 text-[var(--primary)]" : "text-[var(--text-dim)]"}`}>Raw</button>
                      </div>
                    )}
                    <ValidationBadge v={result.validation} />
                    <Button size="sm" variant="ghost" onClick={clearWorkspace} data-testid="new-generation-btn"
                      className="h-7 rounded-[3px] text-xs gap-1.5 hover:bg-[var(--surface)] text-[var(--primary)]" title="Clear the dashboard and start a fresh generation (this result stays saved under the client)">
                      <FilePlus size={13} />New</Button>
                    <Button size="sm" variant="ghost" onClick={copyCode} data-testid="copy-code-btn"
                      className="h-7 rounded-[3px] text-xs gap-1.5 hover:bg-[var(--surface)]"><Copy size={13} />Copy</Button>
                    <a href={api.manualUrl(result.id)} data-testid="download-manual-btn">
                      <Button size="sm" variant="ghost" className="h-7 rounded-[3px] text-xs gap-1.5 hover:bg-[var(--surface)] text-[var(--text)]">
                        <FilePdf size={13} />Manual
                      </Button>
                    </a>
                    <a href={api.downloadUrl(result.id, "primary")} data-testid="download-lsp-btn">
                      <Button size="sm" className="h-7 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                        <Download size={13} />{result.code_alt ? `${shortPlat(result.platform)} ${result.version}` : ".lsp"}
                      </Button>
                    </a>
                    {result.code_alt && (
                      <a href={api.downloadUrl(result.id, "alt")} data-testid="download-lsp-alt-btn">
                        <Button size="sm" className="h-7 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                          <Download size={13} />{shortPlat(result.alt_platform || "progeCAD")} {result.alt_version || result.version}
                        </Button>
                      </a>
                    )}
                    {result.code_third && (
                      <a href={api.downloadUrl(result.id, "third")} data-testid="download-lsp-third-btn">
                        <Button size="sm" className="h-7 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                          <Download size={13} />{shortPlat(result.third_platform || "AutoCAD")} {result.third_version || ""}
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <TabsContent value="code" className="h-full m-0 data-[state=inactive]:hidden flex flex-col">
                    {result.command_clashes?.length > 0 && (
                      <div data-testid="clash-warning" className="shrink-0 flex items-center gap-2 px-4 py-2 bg-[var(--alert)]/10 border-b border-[var(--alert)]/40 text-[var(--alert)] text-[12px] font-mono">
                        <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                        <span className="flex-1">Command name clash: <b>c:{result.command_clashes.join(", c:")}</b> already exist{result.command_clashes.length > 1 ? "" : "s"} in the base program. Rename before loading or it will override the existing command.</span>
                        <select value={clashStyle} onChange={(e) => setClashStyle(e.target.value)} data-testid="clash-style"
                          className="shrink-0 h-6 rounded-[3px] bg-[var(--bg)] border border-[var(--alert)]/40 text-[var(--alert)] text-[10px] font-mono px-1">
                          <option value="suffix">#suffix</option>
                          <option value="prefix">prefix</option>
                        </select>
                        {clashStyle === "prefix" && (
                          <input value={clashPrefix} onChange={(e) => setClashPrefix(e.target.value)} data-testid="clash-prefix" placeholder="ACM"
                            className="shrink-0 w-16 h-6 rounded-[3px] bg-[var(--bg)] border border-[var(--alert)]/40 text-[var(--alert)] text-[10px] font-mono px-1.5 focus:outline-none" />
                        )}
                        <button onClick={handleRenameClash} data-testid="rename-clash-btn"
                          className="shrink-0 h-6 px-2 rounded-[3px] border border-[var(--alert)]/60 hover:bg-[var(--alert)]/20 transition-colors text-[11px] whitespace-nowrap">
                          Auto-rename & re-download
                        </button>
                      </div>
                    )}
                    {result.client_clashes?.length > 0 && (
                      <div data-testid="client-clash-warning" className="shrink-0 flex items-start gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/40 text-amber-500 text-[12px] font-mono">
                        <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                        <span>Also defined in earlier routines for this client: <b>c:{result.client_clashes.join(", c:")}</b> — you may be duplicating existing client work. Open Client History to compare before downloading.</span>
                      </div>
                    )}
                    <ScrollArea className="flex-1 min-h-0"><LispCode code={(codeView === "raw" && result.code_raw) ? result.code_raw : result.code} /></ScrollArea>
                  </TabsContent>
                  <TabsContent value="diagram" className="h-full m-0 data-[state=inactive]:hidden">
                    <ScrollArea className="h-full">
                      <div className="p-6 flex flex-col items-center" data-testid="diagram-content">
                        <div className="text-[11px] font-mono text-[var(--text-dim)] border-l-2 border-[var(--primary)] pl-2 mb-3 self-start">A visual sketch of the routine's workflow / geometry to help you picture the result.</div>
                        {result.diagram_svg
                          ? <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-[4px] p-6 max-w-full [&_svg]:max-w-full [&_svg]:h-auto" dangerouslySetInnerHTML={{ __html: sanitizeSvg(result.diagram_svg) }} />
                          : <div className="text-[var(--text-dim)] text-sm mt-10">No diagram was produced for this routine.</div>}
                        <div className="text-[11px] text-[var(--text-dim)] font-mono mt-3">Figure — routine workflow / geometry</div>
                      </div>
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="usage" className="h-full m-0 data-[state=inactive]:hidden">
                    <ScrollArea className="h-full"><div className="p-5" data-testid="usage-content"><div className="text-[11px] font-mono text-[var(--text-dim)] border-l-2 border-[var(--primary)] pl-2 mb-3">Step-by-step: how to load this routine into your CAD and run the command.</div><div className="text-[14px] leading-relaxed whitespace-pre-wrap text-[var(--text)]/90">{result.usage || "—"}</div></div></ScrollArea>
                  </TabsContent>
                  <TabsContent value="explanation" className="h-full m-0 data-[state=inactive]:hidden">
                    <ScrollArea className="h-full"><div className="p-5" data-testid="explanation-content"><div className="text-[11px] font-mono text-[var(--text-dim)] border-l-2 border-[var(--primary)] pl-2 mb-3">Plain-English walkthrough of what each part of the code does.</div><div className="text-[14px] leading-relaxed whitespace-pre-wrap text-[var(--text)]/90">{result.explanation || "—"}</div></div></ScrollArea>
                  </TabsContent>
                  <TabsContent value="warnings" className="h-full m-0 data-[state=inactive]:hidden">
                    <ScrollArea className="h-full"><div className="p-5 text-[13px] leading-relaxed whitespace-pre-wrap font-mono text-[var(--warn)]" data-testid="warnings-content">{result.warnings || "None"}</div></ScrollArea>
                  </TabsContent>
                  {result.code_alt && (
                    <TabsContent value="alt" className="h-full m-0 data-[state=inactive]:hidden">
                      <ScrollArea className="h-full">
                        <div className="px-4 pt-3 text-[11px] font-mono text-[var(--text-dim)]" data-testid="alt-code-label">Variant for {shortPlat(result.alt_platform || "progeCAD")} {result.alt_version || result.version}</div>
                        <LispCode code={result.code_alt} />
                      </ScrollArea>
                    </TabsContent>
                  )}
                  {result.code_third && (
                    <TabsContent value="third" className="h-full m-0 data-[state=inactive]:hidden">
                      <ScrollArea className="h-full">
                        <div className="px-4 pt-3 text-[11px] font-mono text-[var(--text-dim)]" data-testid="third-code-label">Variant for {shortPlat(result.third_platform || "AutoCAD")} {result.third_version || ""}</div>
                        <LispCode code={result.code_third} />
                      </ScrollArea>
                    </TabsContent>
                  )}
                </div>
              </Tabs>
            )}
          </div>

          {/* Prompt dock — only at the bottom while a result is shown or generating; in the empty state it lives inside the guide */}
          {(result || loading) && (
            <div className="border-t border-[var(--border)] bg-[var(--surface-2)] shrink-0">
              {promptDock}
            </div>
          )}
        </main>

        {/* Right: citations + history */}
        <aside className="hidden lg:flex col-span-3 min-w-0 flex-col min-h-0 bg-[var(--surface)]">
          <div className="flex-1 min-h-0 flex flex-col">
            <Panel title="Documentation Used" testid="citations-panel" icon={<Quotes size={13} weight="bold" />}>
              <ScrollArea className="h-full scroll-block">
                {!result && <div className="p-3 text-[11px] text-[var(--text-dim)] leading-relaxed">Citations of the exact manual sections used to ground the generated code will appear here.</div>}
                {result?.citations?.map((c, i) => (
                  <div key={i} className="px-3 py-2.5 border-b border-[var(--border)]/50 fade-in" data-testid={`citation-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12px] font-medium truncate text-[var(--primary)]">{c.manual_name}</span>
                      <Badge variant="outline" className="rounded-[2px] text-[9px] font-mono border-[var(--border)] text-[var(--text-dim)] px-1 py-0 shrink-0">§{c.chunk_index} · {(c.score * 100).toFixed(0)}%</Badge>
                    </div>
                    <p className="text-[11px] text-[var(--text-dim)] mt-1 leading-snug font-mono">{c.snippet}</p>
                  </div>
                ))}
              </ScrollArea>
            </Panel>
          </div>
          <div className="h-[40%] min-h-0 flex flex-col border-t border-[var(--border)]">
            <Panel title="Client History" testid="history-panel" icon={<ClockCounterClockwise size={13} weight="bold" />}>
              <ScrollArea className="h-full scroll-block">
                {history.length === 0 && <div className="p-3 text-[11px] text-[var(--text-dim)]">{clientId ? "No generations for this client yet." : "Select a client to see their generations."}</div>}
                {history.map((h) => (
                  <div key={h.id} className="px-3 py-2 border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] transition-colors" data-testid={`history-${h.id}`}>
                    <button onClick={() => openHistory(h.id)} data-testid={`history-open-${h.id}`}
                      className="text-[12px] truncate block w-full text-left hover:text-[var(--primary)] transition-colors" title="Reopen this generation">{h.prompt}</button>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="rounded-[2px] text-[9px] font-mono border-[var(--border)] text-[var(--text-dim)] px-1 py-0">{h.platform} {h.version}</Badge>
                      {h.scale ? <Badge variant="outline" data-testid={`history-scale-${h.id}`} className="rounded-[2px] text-[9px] font-mono border-[var(--primary)]/50 text-[var(--primary)] px-1 py-0">1"={h.scale}'</Badge> : null}
                      {h.validation?.ok
                        ? <CheckCircle size={12} className="text-[var(--success)]" />
                        : <Warning size={12} className="text-[var(--alert)]" />}
                      {!h.validation?.ok && (
                        <button onClick={() => { toast.message("Re-running to clear errors…"); handleGenerate({ prompt: h.prompt, platform: h.platform, version: h.version, scale: h.scale ?? null }); }}
                          data-testid={`history-rerun-${h.id}`} title="Re-run this prompt to clear the errors"
                          className="flex items-center gap-1 text-[10px] text-[var(--warn)] hover:text-[var(--primary)] font-mono">
                          <ArrowsClockwise size={11} weight="bold" />Re-run
                        </button>
                      )}
                      <a href={api.downloadUrl(h.id)} className="text-[10px] text-[var(--primary)] hover:underline font-mono ml-auto" data-testid={`history-download-${h.id}`}>.lsp</a>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </Panel>
          </div>
        </aside>
      </div>
    </div>
  );
}

const ValidationBadge = ({ v }) => {
  if (!v) return null;
  const ok = v.ok;
  const badge = (
    <Badge data-testid="validation-badge"
      className={`rounded-[2px] text-[10px] font-mono gap-1 px-1.5 ${ok ? "bg-[var(--success)]/15 text-[var(--success)]" : "bg-[var(--alert)]/15 text-[var(--alert)]"} border-0 cursor-help`}>
      {ok ? <CheckCircle size={11} weight="fill" /> : <Warning size={11} weight="fill" />}
      {ok ? "Valid" : `${v.errors.length} error${v.errors.length !== 1 ? "s" : ""}`}
    </Badge>
  );
  const details = [
    ...(v.errors || []).map((e) => ["error", e]),
    ...(v.warnings || []).map((w) => ["warn", w]),
  ];
  if (!details.length) return badge;
  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild><span>{badge}</span></TooltipTrigger>
        <TooltipContent className="max-w-sm bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text)]" data-testid="validation-details">
          <div className="flex flex-col gap-1 text-[11px] font-mono">
            {details.map(([kind, msg], i) => (
              <div key={i} className={kind === "error" ? "text-[var(--alert)]" : "text-[var(--warn)]"}>
                {kind === "error" ? "✕ " : "! "}{msg}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

function App() {
  const [user, setUser] = useState(undefined); // undefined=checking, null=guest, object=authed

  useEffect(() => {
    authApi.me()
      .then((u) => { markAuthed(true); setUser(u); })
      .catch(() => { markAuthed(false); setUser(null); });
  }, []);

  const logout = async () => {
    try { await authApi.logout(); } catch (e) { console.debug("logout request failed (best-effort)", e); }
    markAuthed(false);
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--bg)]">
        <CircleNotch size={28} className="animate-spin text-[var(--primary)]" />
        <Toaster position="top-center" theme="dark" richColors closeButton duration={20000} toastOptions={{ className: "cursor-pointer", style: { cursor: "pointer" } }} />
      </div>
    );
  }
  if (!user) {
    return (
      <>
        <AuthScreen onAuthed={setUser} />
        <Toaster position="top-center" theme="dark" richColors closeButton duration={20000} toastOptions={{ className: "cursor-pointer", style: { cursor: "pointer" } }} />
      </>
    );
  }
  return <Workspace user={user} onLogout={logout} />;
}

export default App;
